<template>
  <div class="award-summary">
    <el-form size="small" :inline="true" class="query-form">
      <el-form-item label="统计月份">
        <el-date-picker
          v-model="monthRange"
          type="monthrange"
          range-separator="至"
          start-placeholder="开始月"
          end-placeholder="结束月"
          value-format="yyyy-MM"
          :clearable="false"
          style="width: 260px"
        />
      </el-form-item>
      <el-form-item label="任务分类">
        <el-select v-model="queryVo.flCode" clearable placeholder="全部" style="width: 140px">
          <el-option
            v-for="item in flCodeOpts"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="query">查询</el-button>
      </el-form-item>
    </el-form>

    <div class="kpi-row">
      <div class="kpi-item" v-for="item in kpiList" :key="item.label">
        <div class="kpi-value">{{ item.value }}</div>
        <div class="kpi-label">{{ item.label }}</div>
        <div class="kpi-sub" v-if="item.sub">{{ item.sub }}</div>
      </div>
    </div>

    <el-row :gutter="16" class="chart-row">
      <el-col :span="14">
        <div class="panel">
          <div class="panel-title">月度奖励趋势（月奖按任务结束月，周奖按周一所在月）</div>
          <div ref="trendChart" class="chart"></div>
        </div>
      </el-col>
      <el-col :span="10">
        <div class="panel">
          <div class="panel-title">奖励类型构成</div>
          <div ref="typeChart" class="chart"></div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row">
      <el-col :span="12">
        <div class="panel">
          <div class="panel-title">奖励金额 Top10（月奖 + 周奖）</div>
          <div ref="topChart" class="chart"></div>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="panel">
          <div class="panel-title">月度明细</div>
          <el-table :data="monthTrend" size="small" stripe max-height="320" border>
            <el-table-column prop="month" label="月份" width="90" />
            <el-table-column prop="participateCount" label="参与人次" width="90" />
            <el-table-column prop="reachCount" label="获奖人次" width="90" />
            <el-table-column label="月奖" min-width="100">
              <template slot-scope="{ row }">
                {{ row.monthAwardAmount | moneyFormat }}元
              </template>
            </el-table-column>
            <el-table-column label="周奖" min-width="100">
              <template slot-scope="{ row }">
                {{ row.weekAwardAmount | moneyFormat }}元
              </template>
            </el-table-column>
            <el-table-column label="合计奖金" min-width="100">
              <template slot-scope="{ row }">
                {{ row.totalAwardAmount | moneyFormat }}元
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getAwardStatSummary } from "@/api";
import { DateUtil } from "@/util";

function defaultMonthRange() {
  const end = DateUtil.format(new Date(), "yyyy-MM");
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 5);
  const start = DateUtil.format(startDate, "yyyy-MM");
  return [start, end];
}

export default {
  name: "AwardSummary",
  computed: {
    ...mapState({
      flCodeOpts: (state) => state.reward.flCodeOpts
    }),
    kpiList() {
      const d = this.data || {};
      const yuan = (v) => `${this.fenToYuan(v)}元`;
      return [
        {
          label: "任务数",
          value: d.taskCount || 0,
          sub: `适用席位 ${d.inviteCount || 0} · 按任务结束月`
        },
        {
          label: "参与 / 获奖",
          value: `${d.participateCount || 0} / ${d.reachCount || 0}`,
          sub: `参与率 ${d.participateRate || 0}% · 完成率 ${d.reachRate || 0}%`
        },
        {
          label: "月奖",
          value: yuan(d.monthAwardAmount),
          sub: `兑奖 ${d.exchangeCount || 0} 人次 · 按任务结束月`
        },
        {
          label: "周奖",
          value: yuan(d.weekAwardAmount),
          sub: `好评 ${d.goodEvaCount || 0} 笔 · 按周一所在月`
        },
        {
          label: "合计奖金",
          value: yuan(d.totalAwardAmount),
          sub: `月奖 + 周奖 · 获奖司机 ${d.awardDriverCount || 0} 人`
        }
      ];
    }
  },
  data() {
    return {
      monthRange: defaultMonthRange(),
      queryVo: {
        flCode: ""
      },
      data: {},
      monthTrend: [],
      trendChart: null,
      typeChart: null,
      topChart: null
    };
  },
  mounted() {
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    [this.trendChart, this.typeChart, this.topChart].forEach((c) => c && c.dispose());
  },
  methods: {
    query() {
      const [startMonth, endMonth] = this.monthRange || [];
      getAwardStatSummary({
        startMonth,
        endMonth,
        flCode: this.queryVo.flCode || undefined
      }).then((res) => {
        this.data = res.data || {};
        this.monthTrend = this.data.monthTrend || [];
        this.$nextTick(() => {
          this.renderTrendChart();
          this.renderTypeChart();
          this.renderTopChart();
        });
      });
    },
    fenToYuan(val) {
      return Number(((val || 0) / 100).toFixed(2));
    },
    ensureChart(refName, cacheKey) {
      if (!this.$refs[refName]) {
        return null;
      }
      if (!this[cacheKey]) {
        this[cacheKey] = echarts.init(this.$refs[refName]);
      }
      return this[cacheKey];
    },
    resizeCharts() {
      [this.trendChart, this.typeChart, this.topChart].forEach((c) => c && c.resize());
    },
    renderTrendChart() {
      const chart = this.ensureChart("trendChart", "trendChart");
      if (!chart) return;
      const list = this.monthTrend || [];
      chart.setOption(
        {
          tooltip: { trigger: "axis" },
          legend: { data: ["月奖", "周奖", "合计奖金", "参与人次"] },
          grid: { left: 50, right: 40, top: 40, bottom: 30 },
          xAxis: { type: "category", data: list.map((i) => i.month) },
          yAxis: [
            { type: "value", name: "金额(元)" },
            { type: "value", name: "人次", splitLine: { show: false } }
          ],
          series: [
            {
              name: "月奖",
              type: "bar",
              data: list.map((i) => this.fenToYuan(i.monthAwardAmount))
            },
            {
              name: "周奖",
              type: "bar",
              data: list.map((i) => this.fenToYuan(i.weekAwardAmount))
            },
            {
              name: "合计奖金",
              type: "bar",
              data: list.map((i) => this.fenToYuan(i.totalAwardAmount))
            },
            {
              name: "参与人次",
              type: "line",
              yAxisIndex: 1,
              data: list.map((i) => i.participateCount || 0)
            }
          ]
        },
        true
      );
    },
    renderTypeChart() {
      const chart = this.ensureChart("typeChart", "typeChart");
      if (!chart) return;
      const list = this.data.typeDist || [];
      chart.setOption(
        {
          tooltip: {
            trigger: "item",
            formatter: (p) => `${p.name}<br/>${p.value} 元 (${p.percent}%)`
          },
          legend: { type: "scroll", bottom: 0 },
          series: [
            {
              type: "pie",
              radius: ["35%", "60%"],
              center: ["50%", "45%"],
              data: list.map((i) => ({
                name: i.label,
                value: this.fenToYuan(i.amount)
              })),
              label: { formatter: "{b}\n{c}元" }
            }
          ]
        },
        true
      );
    },
    renderTopChart() {
      const chart = this.ensureChart("topChart", "topChart");
      if (!chart) return;
      const list = (this.data.topDrivers || []).slice().reverse();
      chart.setOption(
        {
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
          grid: { left: 90, right: 30, top: 20, bottom: 20 },
          xAxis: { type: "value", name: "元" },
          yAxis: {
            type: "category",
            data: list.map((i) => `${i.driverName || "-"}(${i.driverNo || "-"})`)
          },
          series: [
            {
              type: "bar",
              data: list.map((i) => this.fenToYuan(i.amount)),
              label: { show: true, position: "right" }
            }
          ]
        },
        true
      );
    }
  }
};
</script>

<style lang="less" scoped>
.award-summary {
  .query-form {
    margin-bottom: 8px;
  }

  .kpi-row {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .kpi-item {
    flex: 1;
    min-width: 150px;
    margin: 0 8px 8px 0;
    padding: 14px 12px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fafbfc;
    text-align: center;

    .kpi-value {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
      line-height: 1.4;
    }

    .kpi-label {
      margin-top: 6px;
      font-size: 13px;
      color: #606266;
    }

    .kpi-sub {
      margin-top: 4px;
      font-size: 12px;
      color: #909399;
    }
  }

  .chart-row {
    margin-bottom: 12px;
  }

  .panel {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 12px;
    background: #fff;
    min-height: 360px;

    .panel-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 8px;
    }

    .chart {
      height: 300px;
      width: 100%;
    }
  }
}
</style>
